import {
  Box, Button, VStack, useBreakpointValue,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import {
  IBasket, useGetBasketQuery, useRemoveItemMutation, GetBasketDocument,
} from '../../lib/graphql';
import {
  BasketItemsList,
  SeatHoldCountdown,
  BasketSummary,
} from './components';

/**
 * Main basket detail component
 * Displays basket items and summary with responsive layout
 * Uses extracted components for better maintainability and reusability
 */
const Basket = (): JSX.Element => {
  const [removeItemMutation] = useRemoveItemMutation({});
  const { data: basketData, loading: basketLoading } = useGetBasketQuery();
  const router = useRouter();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Early return for loading state
  if (basketLoading) {
    return null;
  }

  // Extract basket data with fallback
  const basket: IBasket = basketData?.getBasket?.basket || {
    subTotal: 0,
    total: 0,
  };

  // Early return if no basket data
  if (!basketData?.getBasket?.basket) {
    return null;
  }

  // Event handlers
  const handleRemoveItem = (itemId: number, itemType: string): void => {
    removeItemMutation({
      variables: { itemId, itemType },
      refetchQueries: [{ query: GetBasketDocument }],
    });
  };

  const handleCheckout = (): void => {
    router.push('/checkout');
  };

  // Determine if basket has items
  const hasItems = basket?.items?.length > 0;

  // Mobile layout
  if (isMobile) {
    return (
      <Box>
        <VStack spacing={4} align="stretch">
          {hasItems ? (
            <>
              <SeatHoldCountdown expiresAt={basket?.seatHoldExpiresAt} />
              <BasketItemsList
                basket={basket}
                onRemoveItem={handleRemoveItem}
                variant="mobile"
              />
              <BasketSummary
                basket={basket}
                variant="mobile"
              />
              <Button
                size="lg"
                onClick={handleCheckout}
                colorScheme="yellow"
                w="full"
              >
                Go to checkout
              </Button>
            </>
          ) : (
            <BasketItemsList
              basket={basket}
              onRemoveItem={handleRemoveItem}
              variant="mobile"
            />
          )}
        </VStack>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box>
      {hasItems ? (
        <VStack spacing={6} align="stretch">
          <SeatHoldCountdown expiresAt={basket?.seatHoldExpiresAt} />
          <BasketItemsList
            basket={basket}
            onRemoveItem={handleRemoveItem}
            variant="desktop"
          />
          <BasketSummary
            basket={basket}
            variant="desktop"
          />
          <Button
            size="lg"
            onClick={handleCheckout}
            colorScheme="yellow"
            py={6}
            fontSize="lg"
            fontWeight="bold"
            w="full"
          >
            Go to checkout
          </Button>
        </VStack>
      ) : (
        <BasketItemsList
          basket={basket}
          onRemoveItem={handleRemoveItem}
          variant="desktop"
        />
      )}
    </Box>
  );
};

export default Basket;
